import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { boardApi } from '@/api/endpoints';
import { Alert } from '@/components/common/State';
import MediaAttach from '@/components/common/MediaAttach';
import { useMediaUpload } from '@/components/common/useMediaUpload';
import { toMediaInputs, type DraftMedia } from '@/utils/media';

const QNA_CATEGORIES = ['상품문의', '배송문의', '교환/반품', '기타'];
const VOC_CATEGORIES = ['배송', '상품 품질', '고객응대', '개선 제안', '기타'];

export default function BoardWritePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const boardType = searchParams.get('boardType') ?? 'QNA';
  const productId = searchParams.get('productId');

  const categories = boardType === 'QNA' ? QNA_CATEGORIES : VOC_CATEGORIES;

  const [form, setForm] = useState({
    category: categories[0],
    title: '',
    content: '',
    secret: false,
  });
  const [media, setMedia] = useState<DraftMedia[]>([]);
  const [error, setError] = useState<string | null>(null);
  const uploader = useMediaUpload('boards');

  const create = useMutation({
    mutationFn: async () => {
      const uploaded = await uploader.upload(media);
      setMedia(uploaded);
      return boardApi.create({
        boardType,
        productId: productId ? Number(productId) : undefined,
        ...form,
        media: toMediaInputs(uploaded),
      });
    },
    onSuccess: (post) => navigate(`/boards/${post.postId}?boardType=${post.boardType}`, { replace: true }),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <h1 className="panel-title">{boardType === 'QNA' ? '상품 문의 작성' : 'VoC 작성'}</h1>

      <div className="card">
        <form
          className="card__body"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          {error && <Alert tone="error">{error}</Alert>}

          <div className="field">
            <label htmlFor="category">유형</label>
            <select
              id="category"
              className="select"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="title">제목 *</label>
            <input
              id="title"
              className="input"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>

          <MediaAttach value={media} onChange={setMedia} progress={uploader.progress} />

          <div className="field">
            <label htmlFor="content">내용 *</label>
            <textarea
              id="content"
              className="textarea"
              required
              style={{ minHeight: 200 }}
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
            />
          </div>

          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 16 }}>
            <input
              type="checkbox"
              checked={form.secret}
              onChange={(e) => setForm({ ...form, secret: e.target.checked })}
            />
            비밀글로 작성 (작성자와 관리자만 볼 수 있습니다)
          </label>

          <div className="form-actions">
            <button type="button" className="btn btn--outline" onClick={() => navigate(-1)}>
              취소
            </button>
            <button type="submit" className="btn btn--primary" disabled={create.isPending}>
              등록
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
